<template>
  <div id="app">
    <transition name="fade" mode="out-in" @after-leave="afterLeave">
      <router-view/>
    </transition>
  </div>
</template>

<script>
export default {
  methods: {
    afterLeave () {
      this.$root.$emit('triggerScroll')
    }
  }
}
</script>

<style lang="scss">

// Import Bulma's core
@import "~bulma/sass/utilities/_all";

.p {
  font-size: 1.25rem;
}

.mt-0 {
  margin-top: 0!important;
}

.mt-1 {
  margin-top: 0.25rem!important;
}

.mt-2 {
  margin-top: 0.50rem!important;
}

.mt-3 {
  margin-top: 0.75rem!important;
}

.mt-4 {
  margin-top: 1rem!important;
}

.mt-5 {
  margin-top: 1.25rem!important;
}

.mt-6 {
  margin-top: 1.5rem!important;
}

.mt-7 {
  margin-top: 1.75rem!important;
}

.is-size-1 {
  font-size: 3rem !important;
}

.is-size-2 {
  font-size: 2.5rem !important;
}

.is-size-3 {
  font-size: 2rem !important;
}

.is-size-4 {
  font-size: 1.5rem !important;
}

.is-size-5 {
  font-size: 1.25rem !important;
}

.is-size-6 {
  font-size: 1rem !important;
}

.is-size-7 {
  font-size: 0.75rem !important;
}


// Set your colors
// $primary: #B7312C; // from PANTONE https://www.pantone.com/color-finder/7620-C
// $primary: #a81126; // from Illustrator color convert result.
// $primary: #00a43d; // from Imaichi Tatsuya Web Presentation file background color.
$primary: #008FD0; // from Miu Satoh
$primary-invert: findColorInvert($primary);
$secondary: lighten($primary, 50%);
$secondary-invert: findColorInvert($secondary);
$twitter: #4099FF;
$twitter-invert: findColorInvert($twitter);

$nav-bg: lighten(#f7f8f8, 20%);
$bg: #EBEBEB;


// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: (
  "white": ($white, $black),
  "black": ($black, $white),
  "light": ($light, $light-invert),
  "dark": ($dark, $dark-invert),
  "primary": ($primary, $primary-invert),
  "info": ($info, $info-invert),
  "success": ($success, $success-invert),
  "warning": ($warning, $warning-invert),
  "danger": ($danger, $danger-invert),
  "twitter": ($twitter, $twitter-invert)
);

$text: #363636;

// Links
$link: $primary;
$link-invert: $primary-invert;
$link-focus-border: $primary;

$family-sans-serif: 'YakuCaltGothic', 'Yu Gothic', 'YuGothic', system-ui, "Hiragino Sans", sans-serif;
$family-serif: 'YakuCaltMincho', 'Yu Mincho', 'YuMincho', serif;

// 最後にbulmaとbuefyをインポートして各セレクタへ適用する
@import "~bulma";
@import "~buefy/src/scss/buefy";

// retina用のbackground-image(背景画像)を定義するmixin
@mixin imgRetina($imageUrl, $extension, $zoom2: True, $zoom3: True, $repeat: no-repeat, $position-x: center, $position-y: center, $size: null, $width: null, $height: null) {
  background-repeat: $repeat;
  background-position-x: $position-x;
  background-position-y: $position-y;

  background-image: url($imageUrl + '.' + $extension);
  @if $zoom2 == True {
    @media
    screen and ( -webkit-min-device-pixel-ratio: 2 ),
    screen and ( min-resolution: ( 96 * 2 + 'dpi' ) ),
    screen and ( min-resolution: ( 2 + 'dppx' ) ) {
      background-image: url($imageUrl + '@' + 2 + 'x' + '.' + $extension);
    }
  }
  @if $zoom3 == True {
    @media
    screen and ( -webkit-min-device-pixel-ratio: 3 ),
    screen and ( min-resolution: ( 96 * 3 + 'dpi' ) ),
    screen and ( min-resolution: ( 3 + 'dppx' ) ) {
      background-image: url($imageUrl + '@' + 3 + 'x' + '.' + $extension);
    }
  }

  /* Size or width/height required. */
  @if $size {
    background-size: $size;
  } @else {
    background-size: $width $height;
  }
}

@font-face {
  font-family: 'YakuCaltMincho';
  src: url('assets/font/YakuCaltMincho-R.woff2') format('woff2'), url('assets/font/YakuCaltMincho-R.woff') format('woff');
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: 'YakuCaltMincho';
  src: url('assets/font/YakuCaltMincho-R.woff2') format('woff2'), url('assets/font/YakuCaltMincho-R.woff') format('woff');
  font-weight: bold;
  font-display: swap;
}

@font-face {
  font-family: 'YakuCaltGothic';
  src: url('assets/font/YakuCaltGothic-R.woff2') format('woff2'), url('assets/font/YakuCaltGothic-R.woff') format('woff');
  font-weight: normal;
  font-display: swap;
}

@font-face {
  font-family: 'YakuCaltGothic';
  src: url('assets/font/YakuCaltGothic-R.woff2') format('woff2'), url('assets/font/YakuCaltGothic-R.woff') format('woff');
  font-weight: bold;
  font-display: swap;
}

@font-face {
  font-family: 'lang_zh-tw-sans';
  src: url('assets/font/SourceHanSansTW-Normal.woff') format('woff');
  font-weight: normal;
  font-display: swap;
}

* {
  margin: 0;
  padding: 0;

  &::after {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

img, a img {
  border-style: none;
}

html {
  scroll-behavior: smooth;
  @include until($desktop) {
    padding-top: 56px;
  }
}

body {
  // font-family: 'YakuCaltGothic', 'Yu Mincho', 'YuMincho', 'Hiragino Mincho ProN', 'Hiragino Mincho Pro', 'HGP明朝B', serif;
  font-family: $family-sans-serif;
  background: #ffffff;
  // background: lighten($primary, 55%);
  color: $text;
  font-size: 16px;
  font-weight: 500;
  @include until($desktop) {
    font-weight: 400;
  }
}

a {
  text-decoration: underline;
}

.is-serif {
  font-family: $family-serif;
  text-align: justify;
}

.calt {
	-webkit-font-feature-settings: 'calt' 1;
	-moz-font-feature-settings: 'calt' 1;
	font-feature-settings: 'calt' 1;
}

.home {
  display: flex;
  flex-direction: row;
  @include until($desktop) {
    display: block;
    flex-direction: column;
  }
}

.header {
  width: 100px;
  height: auto;
  line-height: 1;
  z-index: 10;
  padding: 0;
  top: 0;
  left: 0;
  background-color: $nav-bg;
  // @include imgRetina(assets/img/header/header_bg, png, True, True, no-repeat, center, top, null, 100px, 530px);

  @include until($desktop) {
    width: 100%;
    height: 56px;
    // @include imgRetina(assets/img/header/navbar--until_tablet__bg, png, True, True, no-repeat, left, top, null, 284px, 56px);

    position: fixed;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow-y: visible;
  }
  .header__logo {
    display: block;
    width: 100px;
    height: 365px;
    @include imgRetina(assets/img/header/gnav_logo, svg, False, False, no-repeat, center, top, null, 100px, 385px);
    color: transparent;
    user-select: none;
    cursor: pointer;
    @include until($desktop) {
      align-self: flex-start;
      width: 290px;
      height: 56px;
      @include imgRetina(assets/img/header/header__logo--sp, svg, False, False, no-repeat, center, top, null, 290px, 56px);
    }
  }
  .global__nav {
    position: sticky;
    top: 0;
    width: 100px;
    padding-top: 20px;
    transform-origin: top;
    @media (max-height: 729px) {
      transform: scale(0.75);
    }
    @media (max-height: 547px) {
      transform: scale(0.5);
    }
    @include until($desktop) {
      display: none;
    }
    .gnav__item {
      width: 100px;
      margin-bottom: 24px;
      display: block;
      color: transparent;
      user-select: none;
      transition: opacity 0.2s ease-in-out;
      &:hover {
        opacity: 0.5;
      }
      // &.gnav__youtube {
      //   @include imgRetina(assets/img/header/gnav-youtube, png, True, True, no-repeat, center, center, contain, null, null);
      //   height: 34px;
      //   margin: 0;
      //   position: fixed;
      //   left: 0;
      //   bottom: 0;
      // }
      &.gnav__01 {
        @include imgRetina(assets/img/header/gnav_01, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 55px;
      }
      &.gnav__02 {
        @include imgRetina(assets/img/header/gnav_02, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 142px;
      }
      &.gnav__03 {
        @include imgRetina(assets/img/header/gnav_03, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 55px;
      }
      &.gnav__04 {
        @include imgRetina(assets/img/header/gnav_04, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 165px;
      }
      &.gnav__05 {
        @include imgRetina(assets/img/header/gnav_05, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 98px;
      }
      &.gnav__06 {
        @include imgRetina(assets/img/header/gnav_06, svg, False, False, no-repeat, center, center, contain, null, null);
        height: 74px;
      }
    }
  }
  .navbar-end {
    display: none;
    @include until($desktop) {
      display: block;
      align-self: flex-end;
      width: 56px;
      height: 56px;
      .dropdown-trigger button {
        display: block;
        width: 56px;
        height: 56px;
        padding: 0;
        background-color: transparent;

        &:focus:not(:active),
        &.is-focused:not(:active) {
          box-shadow: none;
        }

        .header__dropdownbtn__icon {
          user-select: none;
          color: transparent;
          font-size: 0;
          display: block;
          width: 56px;
          height: 56px;
          padding: 0;
          @include imgRetina(assets/img/header/menu--open, svg, False, False, no-repeat, center, center, contain, null, null);
          &.menu-up {
            @include imgRetina(assets/img/header/menu--close, svg, False, False, no-repeat, center, center, contain, null, null);
          }
        }
      }
      .dropdown-menu {
        z-index: 50;
        a {
          text-decoration: none;
          color: $primary;
        }
        a:hover {
          background-color: lighten($primary, 55%);
        }
      }
    }
  }
}

.mainContent {

  .fullwidth--wrap {
    &.bgc {
      background-color: lighten($bg, 5%);
      padding: 60px 0 0;
    }
  }
  width: calc(100% - 100px);
  @include until($desktop) {
    width: 100%;
  }
  section {
    margin: 60px auto;
    padding: 0 0 60px;
    &.hr {
      border-bottom: 1px solid $bg;
    }
    max-width: 1098px;
    @include until($fullhd) {
      max-width: 900px;
    }
    @include until($widescreen) {
      max-width: 800px;
    }
    @include until($desktop) {
      margin: 2.5em auto;
      padding: 0 0 1em;
    }
    &.topimg {
      position: relative;
      margin: 0;
      padding: 0;
      max-width: 100%;

      .landscape {
        background: $primary;
        height: 650px;
        overflow: hidden;
        position: relative;
        @include until($desktop) {
          height: 550px;
        }
        display: flex;
        flex-direction: column;
        align-items: center;

        left: 0;
        top: 0;
        position: relative;
        width: 100%;
      }

      .landscape__layer {
        // parallax
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        flex-direction: column;

        align-items: center;

        transform: translateY(calc(var(--scrollPos, 0) * var(--offset, 0)));
        @media (prefers-reduced-motion: reduce) {
          transform: translateY(0);
        }
      }

      .landscape__image {

        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translate(-50%, 0);

        max-height: 90%;
        max-width: 160%;
        min-width: 104%;
        width: 1920px;

        @include until($desktop) {
          max-height: 90%;
          max-width: 160%;
        }
      }

      .landscape__image img {
        display: block;
        height: auto;
        max-width: 100%;
      }

      .landscape__title {
        z-index: 9;
        width: 250px;
        top: 40px;
        position: relative;
        @include until($desktop) {
          width: 220px;
          top: 30px;
        }
      }

      .landscape__mojijuku {
        z-index: 11;
        width: 300px;
        bottom: 2rem;
        position: absolute;
      }

    }
    &.period,
    &.about {
      text-align: center;
    }
    &.period {
      .timetable {
        max-width: 512px;
        margin-right: auto;
        margin-left: auto;
        margin-bottom: 0rem;
        @include until($desktop) {
          padding-right: 16px;
          padding-left: 16px;
        }
      }
      .description {
        font-size: 1.25rem;
        @include until($tablet) {
          font-size: 1rem;
        }
      }
      .description--small {
        font-size: 1rem;
        @include until($tablet) {
          font-size: 0.9rem;
        }
      }
      .namejob {
        .name,
        .job {
          font-size: 1.25rem;
          @include until($tablet) {
            font-size: 1rem;
          }
        }
      }
      .centerColon {
        padding: 0 0.5em;
        flex-grow: 2;
        position: relative;
        hr {
          background-color: transparent;
          border-width: 1px 0px 0px 0px;
          border-style: solid;
          border-color: #ccc;
          margin: 0;
          height: 1px;
          position: relative;
          top: 1.25rem;
          @include until($tablet) {
            top: 0.9rem;
          }
        }
      }
    }
    &.about {
      .namejob {
        flex-direction: column;
        align-items: center;
      }
    }
    &.works {
      text-align: center;
      border-bottom: 0;
      padding-bottom: 44px;
      @include until($desktop) {
        border-bottom: 0;
        &:after {
          content: unset;
          display: unset;
          height: unset;
          margin-right: unset;
          margin-left: unset;
          background-color: unset;
        }
      }
      .works__thumbnail {
        width: 320px;
        max-width: 660px;
        margin: 2em 0 5em;
      }
    }
    &.eyecatch {
      position: relative;
      height: 750px;
      margin: 0;
      border: 0;
      padding: 0;
      max-width: 100%;
      @include until($desktop) {
        position: relative;
        height: 480px;
        border-bottom: 0;
        &:after {
          content: unset;
          display: unset;
          height: unset;
          margin-right: unset;
          margin-left: unset;
          background-color: unset;
        }
      }
      .slideshow {
        overflow: hidden;
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position:center center;
        background-repeat: no-repeat;
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;

        --bg1: url("assets/img/slider_photo/slider_photo-01.jpg");
        --bg2: url("assets/img/slider_photo/slider_photo-02.jpg");
        --bg3: url("assets/img/slider_photo/slider_photo-03.jpg");
        --bg4: url("assets/img/slider_photo/slider_photo-04.jpg");
        --bg5: url("assets/img/slider_photo/slider_photo-05.jpg");
        --bg6: url("assets/img/slider_photo/slider_photo-06.jpg");
        --bg7: url("assets/img/slider_photo/slider_photo-07.jpg");
        --bg8: url("assets/img/slider_photo/slider_photo-08.jpg");
        --bg9: url("assets/img/slider_photo/slider_photo-09.jpg");

        --current-bg: var(--bg1);
        --numberOfImages: 9;
        --duration: 32s;
        --fadeDuration: calc(var(--duration) / var(--numberOfImages));
        --delay-duration: calc(var(--fadeDuration) / 2);

        background-image: var(--current-bg);

        animation: changeImage var(--duration) linear infinite,
          fade var(--fadeDuration) linear infinite;
      }

      .slideshow2 {
        animation: changeImage var(--duration) var(--delay-duration) linear infinite,
          fade var(--fadeDuration) var(--delay-duration) linear infinite;
      }

      /* Animate using css custom properties */
      @keyframes changeImage {
        0% {
          --current-bg: var(--bg1);
        }
        11.11% {
          --current-bg: var(--bg2);
        }
        22.22% {
          --current-bg: var(--bg3);
        }
        33.33% {
          --current-bg: var(--bg4);
        }
        44.44% {
          --current-bg: var(--bg5);
        }
        55.55% {
          --current-bg: var(--bg6);
        }
        66.66% {
          --current-bg: var(--bg7);
        }
        77.77% {
          --current-bg: var(--bg8);
        }
        88.88% {
          --current-bg: var(--bg9);
        }
        100% {
          --current-bg: var(--bg1);
        }
      }

      @keyframes fade {
        25% {
          opacity: 1;
        }
        50% {
          opacity: 0;
        }
        75% {
          opacity: 1;
        }
      }
    }
    &.pamphlet {
      text-align: center;
      .pamphlet__thumbnail__wrap {
        // display: flex;
        width: 100%;
        max-width: 753px;
        margin: 4em auto;
        flex-wrap: nowrap;
        justify-content: space-between;
        img {
          box-shadow: rgba(58, 57, 56, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px;
          width: 47%;
          max-width: 350px;
          &.full-width {
            width: 100%;
            max-width: inherit;
          }
        }
      }
      p.caption {
        text-indent: 1em;
        @include until($desktop) {
          font-size: 1.1rem;
          margin-bottom: 2rem;
        }
      }
      .button__wrapper {
        @include until($desktop) {
          margin-bottom: 2em;
        }
      }
      .typeface__commentator {
        background-color: lighten($bg, 5%);
        border-radius: 8px;
        padding: 16px 40px 16px;
        margin-top: 40px;
        @include until($desktop) {
          margin: 16px 16px 0;
          padding: 16px 0px 16px;
        }
        .profile__title {
          .h3 {
            text-align: left;
          }
          .column {
            @include until($desktop) {
              padding: 16px 16px;
            }
            .komiyama__profile {
              @include until($desktop) {
                font-size: 1.1rem;
              }
            }
          }
        }
        .profile__thumbnail {
          border-radius: 6px;
          width: 200px;
        }
      }
    }
    &.talkevent {
      margin-top: 120px;
      text-align: center;
      .h1 {
        font-size: 2.15rem;
        @include until($desktop) {
          font-size: 1.5rem;
        }
      }
      .h3 {
        margin-top: 2rem;
        margin-bottom: 0;
        @include until($desktop) {
          margin-top: 2em;
        }
      }
      p.caption {
        @include until($desktop) {
          margin-top: 1rem;
          font-size: 1.1rem;
        }

        &.notes {
          line-height: 1.8;
          font-size: 1rem;

          @include until($desktop) {
            font-size: 0.9rem;
          }
        }
      }
      .talk__guest {
        background-color: lighten($bg, 5%);
        border-radius: 8px;
        padding: 30px 60px 60px;
        margin-top: 40px;
        @include until($desktop) {
          margin: 16px 16px 0;
          padding: 16px 0px 64px;
        }
        .profile__title {
          .h3 {
            text-align: left;
            margin-top: 1rem;
            margin-bottom: 1rem;
            @include until($desktop) {
              margin-top: 2rem;
            }
          }
          p {
            @include until($desktop) {
              font-size: 1.1rem;
            }
          }
        }
        .profile__thumbnail__wrap {
          margin-top: 32px;
          text-align: left;
          @include until($desktop) {
            margin-top: 0;
          }
        }
        .profile__thumbnail {
          border-radius: 6px;
          width: 200px;
          text-align: left;
          @include until($desktop) {
            margin-top: 2rem;
          }
        }
      }
      .timetable {
        max-width: 512px;
        margin-right: auto;
        margin-left: auto;
      }
      .namejob {
        &+.description {
          text-align: left;
        }
      }
      .columns {
        .column {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
      div.caption {
        margin-top: 1rem;
        text-indent: 1em;
        p {
          @include until($desktop) {
            font-size: 1.1rem;
          }
        }
      }
      .additional__event {
        border-radius: 8px;
        padding: 32px;
        max-width: 900px;
        margin: 40px auto;
        margin-top: 40px;
        @include until($desktop) {
          margin: 16px;
          padding: 32px 0;
        }
        .event__title {
          .h3 {
            text-align: center;
            font-size: 1.3rem;
            margin-top: 0;
            @include until($desktop) {
              font-size: 1.1rem;
            }
          }
        }
      }
    }
    &.access {
      text-align: center;
      margin-top: 0;
      margin-bottom: 60px;
      @include until($desktop) {
        padding-right: 32px;
        padding-left: 32px;
      }
      p {
        @include until($desktop) {
          font-size: 1.1rem;
        }
      }
      .column {
        display: flex;
        flex-direction: column;
      }
      .upper__wrap {
        display: flex;
        flex-direction: column;
      }
      .text__wrap {
        flex-grow: 1;
      }
      .map__wrap {
        width: 100%;
        max-width: 750px;
        min-height: 400px;
        margin: 0 auto;
        margin-bottom: 1em;
        margin-top: -1em;
        @include until($desktop) {
          margin: 0 auto;
          margin-bottom: 1em;
          margin-top: -1em;
          min-height: 0;
        }
        img {
          &.full-width {
            width: 100%;
          }
        }
        iframe {
          width: 100%;
          height: 100vh;
          max-height: 400px;
          @include until($desktop) {
            height: 50vh;
          }
          @include until($tablet) {
            height: 50vh;
          }
        }
        filter: grayscale(100%);
      }
    }
    &.qanda {
      text-align: center;
      .button__wrapper {
        margin-top: 4em;
        margin-bottom: -2em;
      }
      .qawrap {
        padding-top: 30px;
        // border-top: solid 1px $bg;
        margin: 30px 0;
        .h3 {
          text-align: left;
          padding-right: 0;
          padding-left: 0;
          @include until($desktop) {
            padding-right: 32px;
            padding-left: 32px;
          }
        }
        &+.qawrap {
        }
      }
      p.caption {
        @include until($desktop) {
          font-size: 1.1rem;
        }
      }
    }

    .hash_anchor {
      position: relative;
      top: -80px;
      display: block;
      height: 0;
      width: 0;
      @include until($desktop) {
        top: -120px;
      }
    }
    .caption {
      margin-right: auto;
      margin-left: auto;
      @include until($desktop) {
        padding-right: $gap;
        padding-left: $gap;
      }
    }
    .period {
      font-feature-settings: 'ss01' 1, 'ss02' 1;
    }
    .date {
      color: lighten($black, 10%);
      overflow: visible;
      white-space: nowrap;
      margin-bottom: 1rem;
      @include until($desktop) {
        overflow: visible;
        white-space: normal;
        margin-bottom: 0rem;
      }
      font-size: 24px;
      line-height: 1.5;
      @include until($desktop) {
        font-size: 20px;
      }
      font-feature-settings: 'ss01' 1, 'ss02' 1;
      font-weight: 500;
      margin: 0 0.5em;
      span {
        font-size: 30px;
        @include until($desktop) {
          font-size: 26px;
        }
        &.time {
          font-size: 32px;
          @include until($desktop) {
            font-size: 20px;
          }
          padding-left: 0px;
        }
      }
      &.date--small {
        font-size: 14px;
        span {
          font-size: 20px;
        }
        @include until($desktop) {
          font-size: 14px;
          span {
            font-size: 18px;
          }
        }
      }
    }
    .field {
      margin-bottom: 2rem;
      .tag,
      .tag span {
        font-size: 18px;
        font-weight: bold;
        @include until($tablet) {
          font-size: 0.9rem;
        }
      }
      .tag {
        background-color: #ddd;
        color: #363636;
      }
    }
    .button__wrapper {
      margin-top: 2em;
      .button {
        text-align: center;
        text-decoration: none;
        height: auto;
        letter-spacing: 0.1em;
        font-family: $family-sans-serif;
        font-weight: bold;
        line-height: 1.5;
        border-radius: 20px;
        border: 0;
        background-color: $primary;
        color: $white;
        padding-bottom: 0.5em;
        padding-left: 3em;
        padding-right: 3em;
        padding-top: 0.4em;
        margin: 0 1.75em 1em;
        min-width: 250px;
        @include until($desktop) {
          padding-left: 1em;
          padding-right: 1em;
          min-width: 250px;
        }
        @include until($tablet) {
          margin-left: 0.5em;
          margin-right: 0.5em;
        }
        transition: background-color 0.2s ease-in-out;
        &:hover {
          color: $primary;
          background-color: $secondary;
        }
      }
    }
    .columns {
      @include until($desktop) {
        margin-right: 0;
        margin-left: 0;
      }
      .column {
        padding: $gap;
      }
    }
    .h1 {
      color: lighten($black, 10%);
      font-size: 36px;
      font-weight: 700;
      letter-spacing: 0.1em;
      margin-bottom: 2rem;
      .is-small {
        position: relative;
        top: -8px;
      }
      a {
        text-decoration: none;
        color: $text;
        &:hover {
          color: $link;
          text-decoration: underline;
        }
      }
    }
    .h2 {
      color: lighten($black, 10%);
      font-size: 2rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      margin-bottom: 2rem;
      .is-small {
        position: relative;
        top: -4px;
      }
    }
    .h3 {
      color: lighten($black, 10%);
      font-size: 21px;
      font-weight: 700;
      letter-spacing: 0.025em;
      margin-bottom: 1rem;
      .is-small {
        position: relative;
        top: -2px;
      }
    }
    .h3__margin {
      color: lighten($black, 10%);
      font-size: 21px;
      font-weight: 700;
      letter-spacing: 0.025em;
      margin-top: 3rem;
      margin-bottom: 1rem;
      .is-small {
        position: relative;
        top: -2px;
      }
    }
    p {
      font-size: 1.25rem;
      line-height: 1.75;
    }
    .is-small {
      font-size: 14px;
    }
    .note {
      color: $primary;
      font-size: 14px;
      margin-bottom: 1em;
      @include until($desktop) {
        padding-right: $gap;
        padding-left: $gap;
      }
    }
    .about__description {
      margin-top: 3em;
      @include until($desktop) {
        margin-top: 2em;
      }
      text-align: left;
      .h3 {
        margin-bottom: 1em;
        text-align: center;
      }
      .h3__margin {
        margin-bottom: 1em;
        text-align: center;
      }
      .torinoumimessage p {
        text-indent: 1em;
        &.has-text-right {
          text-indent: 0;
        }
        @include until($desktop) {
          font-size: 1.1rem;
        }
      }
      .student {
        font-size: 1.25rem;
        line-height: 2.5rem;
      }
    }
    .namejob {
      // width: 90%;
      // color: #330101;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
      // background-color: #fbfbfb;
      // font-size: 14px;
      font-feature-settings: 'ss02' 1;
      margin-bottom: 1.095rem;
      &.with--description {
        margin-bottom: 0;
      }
      &+.description {
        margin-bottom: 1.095rem;
        text-align: right;
      }
      .name,
      .job {
        font-size: 1.25rem;
        flex: 0 1 auto;
        line-height: 1.75;
        font-feature-settings: 'ss01' 1, 'ss02' 1;
        @include until($tablet) {
          font-size: 1rem;
        }
      }
      .is-center {
        text-align: center;
      }
      .centerLine {
        padding: 0 0.5em;
        flex-grow: 2;
        position: relative;
        hr {
          background-color: transparent;
          border-width: 1px 0px 0px 0px;
          border-style: solid;
          border-color: #ccc;
          margin: 0;
          height: 1px;
          position: relative;
          top: 1.25rem;
          @include until($tablet) {
            top: 0.9rem;
          }
        }
        > p {
          font-size: 12px;
          text-align: right;
          width: 100%;
        }
      }
      > p {
        width: 100%;
      }
    }
  }
  > footer {
    // margin: 60px auto 76px;
    margin: auto;
    text-align: center;
    padding: 64px 32px;
    background-color: $primary;
    color: $white;
  }
}

.works__modal {
  .modal-content {
    overflow: visible;
    -ms-overflow-style: none;
  }
}

.works__thumb__header {
  color: rgba(255,255,255,0.8);
  text-align: center;
  width: 100%;
  margin-bottom: 0.5em;
}
.works__thumb__wrap {
  display: flex;
  width: 683px;
  @include until($tablet) {
    width: 320px;
    border: 0;
  }
  align-content: flex-start;
  flex-wrap: wrap;
  background-color: #ddd;
  .works__thumb__item {
    background-color: $white;
    max-width: 170px;
    min-width: 170px;
    img {
      width: 170px;
      position: relative;
      transition: transform 0.15s ease-in-out;
      z-index: 41;
      box-shadow: none;
      &:hover {
        z-index: 50;
        transform: scale(1.12, 1.12);
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
      }
    }
    @include until($tablet) {
      max-width: 106px;
      min-width: 106px;
      img {
        width: 106px;
      }
    }
    line-height: 0;
    margin-right: 1px;
    &:nth-child(4n) {
      margin-right: 0;
    }
    @include until($tablet) {
      &:nth-child(4n) {
        margin-right: 1px;
      }
      &:nth-child(3n) {
        margin-right: 0;
      }
    }
    margin-bottom: 1px;
    &:nth-last-child(1),
    &:nth-last-child(2),
    &:nth-last-child(3),
    &:nth-last-child(4) {
      margin-bottom: 0;
    }
    @include until($tablet) {
      &:nth-last-child(4) {
        margin-bottom: 1px;
      }
    }
    display: block;
    flex-basis: 0;
    flex-grow: 1;
    flex-shrink: 1;
  }
}

.portfolio__wrap {
  width: 100%;
  max-height: 100%;
  @include until($desktop) {
    margin: 0 auto;
  }
  .portfolio {
    width: 90vw;
    background-color: $white;
    z-index: 40;
    position: relative;
    margin: 4vw auto;
    padding: 0 0 20px;

    img {
      width: 100%;
      box-shadow: 0 0 1vw 0 rgba(0,0,0,0.1);
    }
    .img-item {
      margin: 0;
      &+.img-item {
        margin: 4vw 0 0;
      }
    }
    p {
      text-align: justify;
      font-size: 18px;
      max-width: 864px;
    }
    p.caption {
      margin: 2.5vw auto;
      padding: 2.5vw 162px;
      background-color: $white-bis;
      @include until($desktop) {
        font-size: 3.035vw;
        font-weight: 400;
        max-width: 100%;
        padding: 2.5vw 2.5vw;
      }
    }
    .description {
      margin: 2.5vw 0;
      @include until($desktop) {
        font-size: 3.035vw;
        max-width: 100%;
        padding: 2.5vw 2.5vw;
      }
    }
    .description p {
      font-family: $family-serif;
      font-weight: 400;
      margin: 0 auto;
      padding: 0 162px;
      &+p {
        margin-top: 1em;
      }
      @include until($desktop) {
        font-size: 3.035vw;
        max-width: 100%;
        padding: 2.5vw 2.5vw;
      }
    }
  }
  .license {
    position: fixed;
    right: 0;
    bottom: 0;
    z-index: 50;
    color: $text;
    background-color: #ffffff;
    line-height: 1.5;
    border-top-left-radius: 0.25em;
    padding-left: 0.25em;
    opacity: .3;
  }
}

// modal


.modal {
  overflow-y: auto;
  .modal-background {
    min-height: 660px;
    @include until($tablet) {
      min-height: 640px;
    }
  }
}

.modal.is-full-screen > .animation-content,
.modal.is-full-screen > .animation-content > .modal-card {
  background-color: $white;
}
.modal .animation-content {
  max-width: unset;
  width: unset;
  overflow: unset;
}
.modal-content {
  .is-large.modal-close,
  .is-large.delete {
    height: 5vw;
    max-height: 5vw;
    max-width: 5vw;
    min-height: 5vw;
    min-width: 5vw;
    width: 5vw;
    z-index: 41;
    @include until($desktop) {
      height: 50px;
      max-height: 50px;
      max-width: 50px;
      min-height: 50px;
      min-width: 50px;
      width: 50px;
    }
  }
  .modal-close::before,
  .delete::before,
  .modal-close::after,
  .delete::after {
    background-color: lighten($black, 70%);
  }
  .modal-close:hover,
  .delete:hover,
  .modal-close:focus,
  .delete:focus {
    background-color: lighten($black, 90%);
  }
  .modal-close::before,
  .delete::before {
    height: 0.2vw;
    @include until($desktop) {
      height: 2px;
    }
  }
  .modal-close::after,
  .delete::after {
    width: 0.2vw;
    @include until($desktop) {
      width: 2px;
    }
  }
}
.lang_zh-tw {
  font-family: 'lang_zh-tw-sans';
}
</style>
